import {
	coordKey,
	getDirection,
	getNeighbors,
	type HexDirection,
	hexDistance,
	isValidPath,
} from "./hex-grid";
import {
	DEFAULT_MAX_CONSECUTIVE_SAME_DIRECTION_STEPS,
	DEFAULT_PATH_GENERATION_MAX_ATTEMPTS,
	type PathGenerationAttemptOptions,
	type PathGenerationResult,
	type PathGeneratorConfig,
} from "./path-config";
import { createSeededRng, type SeededRng } from "./seeded-rng";
import type { HexCoord } from "./types";

interface MoveCandidateInfo {
	move: HexCoord;
	distanceToDestinationBand: number;
	movesAwayFromCenter: boolean;
	movesTowardCenter: boolean;
	direction: HexDirection;
}

function isMoveAwayFromCenter(
	currentCol: number,
	nextCol: number,
	endCol: number,
): boolean {
	return Math.abs(nextCol - endCol) > Math.abs(currentCol - endCol);
}

function isMoveTowardCenter(
	currentCol: number,
	nextCol: number,
	endCol: number,
): boolean {
	return Math.abs(nextCol - endCol) < Math.abs(currentCol - endCol);
}

function resolveEndRowBounds(config: PathGeneratorConfig): {
	min: number;
	max: number;
} {
	const min = config.endRowMin ?? config.endRow;
	const max = config.endRowMax ?? config.endRow;
	return min <= max ? { min, max } : { min: max, max: min };
}

function isWithinAxisCorridor(
	value: number,
	start: number,
	end: number,
	endMin?: number,
	endMax?: number,
): boolean {
	if (start === end) {
		return true;
	}
	const resolvedEndMin = endMin ?? end;
	const resolvedEndMax = endMax ?? end;
	const corridorMin = Math.min(start, resolvedEndMin);
	const corridorMax = Math.max(start, resolvedEndMax);
	return value >= corridorMin && value <= corridorMax;
}

function isWithinStartEndCorridor(
	row: number,
	col: number,
	config: PathGeneratorConfig,
): boolean {
	const endRowBounds = resolveEndRowBounds(config);
	return (
		isWithinAxisCorridor(
			row,
			config.startRow,
			config.endRow,
			endRowBounds.min,
			endRowBounds.max,
		) && isWithinAxisCorridor(col, config.startCol, config.endCol)
	);
}

function isDestinationCell(
	row: number,
	col: number,
	config: PathGeneratorConfig,
): boolean {
	const bounds = resolveEndRowBounds(config);
	return col === config.endCol && row >= bounds.min && row <= bounds.max;
}

function distanceToDestinationBand(
	row: number,
	col: number,
	config: PathGeneratorConfig,
): number {
	const bounds = resolveEndRowBounds(config);
	let minDistance = Number.POSITIVE_INFINITY;
	for (
		let candidateRow = bounds.min;
		candidateRow <= bounds.max;
		candidateRow += 1
	) {
		minDistance = Math.min(
			minDistance,
			hexDistance(row, col, candidateRow, config.endCol),
		);
	}
	return minDistance;
}

function filterValidPathMoves(
	validMoves: HexCoord[],
	current: HexCoord,
	config: PathGeneratorConfig,
): HexCoord[] {
	const { startRow, endCol, yMinRange, yMaxRange, minCol, maxCol } = config;
	const currentCol = current.col;
	const topRow = startRow + yMinRange;
	const bottomRow = startRow + yMaxRange;
	const filtered = validMoves.filter((n) => {
		if (!isWithinStartEndCorridor(n.row, n.col, config)) {
			return false;
		}
		const rowOffset = n.row - startRow;
		if (rowOffset < yMinRange || rowOffset > yMaxRange) {
			return false;
		}
		if (minCol !== undefined && n.col < minCol) {
			return false;
		}
		if (maxCol !== undefined && n.col > maxCol) {
			return false;
		}
		if (endCol > currentCol && n.col > endCol) {
			return false;
		}
		if (endCol < currentCol && n.col < endCol) {
			return false;
		}
		if (
			isMoveAwayFromCenter(currentCol, n.col, endCol) &&
			(n.row === topRow || n.row === bottomRow)
		) {
			return false;
		}
		return true;
	});

	// Major-axis assist: when we're at the vertical corridor boundary, prefer moves that do not
	// increase distance from the destination band (endRow..endRowMax) when possible.
	// This keeps the spine inside the intended major-range corridor.
	{
		const currentDist = distanceToDestinationBand(
			current.row,
			current.col,
			config,
		);
		const nonWorsening = filtered.filter(
			(n) =>
				distanceToDestinationBand(n.row, n.col, config) <= currentDist,
		);
		if (nonWorsening.length > 0) {
			return nonWorsening;
		}
	}

	// Soft-boundary assist: if we are already at the configured minor-axis boundary (minCol/maxCol),
	// prefer moves that do not increase distance from the centerline (endCol) when possible.
	if (minCol !== undefined || maxCol !== undefined) {
		const currentDist = Math.abs(currentCol - endCol);
		const minDist =
			minCol !== undefined
				? Math.abs(minCol - endCol)
				: Number.POSITIVE_INFINITY;
		const maxDist =
			maxCol !== undefined
				? Math.abs(maxCol - endCol)
				: Number.POSITIVE_INFINITY;
		const boundaryDist = Math.max(0, Math.min(minDist, maxDist));
		if (currentDist >= boundaryDist && boundaryDist > 0) {
			const nonWidening = filtered.filter(
				(n) => !isMoveAwayFromCenter(currentCol, n.col, endCol),
			);
			if (nonWidening.length > 0) {
				return nonWidening;
			}
		}
	}

	return filtered;
}

function isFeasibleStep(
	distanceToBand: number,
	move: HexCoord,
	stepsRemainingAfterMove: number,
	config: PathGeneratorConfig,
): boolean {
	if (stepsRemainingAfterMove === 0) {
		return isDestinationCell(move.row, move.col, config);
	}
	return distanceToBand <= stepsRemainingAfterMove;
}

function pickNextMove(
	candidateInfos: MoveCandidateInfo[],
	rng: SeededRng,
	progress: number,
	consecutiveAwayFromCenter: number,
	consecutiveTowardCenter: number,
	maxConsecutiveCenterDistanceMoves: number,
	previousDirection: HexDirection | null,
	consecutiveSameDirectionSteps: number,
	maxConsecutiveSameDirectionSteps: number,
): HexCoord | undefined {
	if (candidateInfos.length === 0) {
		return undefined;
	}

	let candidates = candidateInfos;
	const candidatesBeforeConsecutiveFilter = candidates;
	candidates = candidates.filter((candidateInfo) => {
		if (
			candidateInfo.movesAwayFromCenter &&
			consecutiveAwayFromCenter >= maxConsecutiveCenterDistanceMoves
		) {
			return false;
		}
		if (
			candidateInfo.movesTowardCenter &&
			consecutiveTowardCenter >= maxConsecutiveCenterDistanceMoves
		) {
			return false;
		}
		return true;
	});
	if (candidates.length === 0) {
		candidates = candidatesBeforeConsecutiveFilter;
	}

	const candidatesBeforeSameDirectionFilter = candidates;
	if (
		previousDirection &&
		consecutiveSameDirectionSteps >= maxConsecutiveSameDirectionSteps
	) {
		candidates = candidates.filter(
			(candidateInfo) => candidateInfo.direction !== previousDirection,
		);
	}
	if (candidates.length === 0) {
		candidates = candidatesBeforeSameDirectionFilter;
	}

	if (candidates.length === 1) {
		return candidates[0]?.move;
	}
	let pool = candidates;
	if (pool.length === 0) {
		return undefined;
	}
	if (progress >= 0.65) {
		const nearestDistance = Math.min(
			...pool.map(
				(candidateInfo) => candidateInfo.distanceToDestinationBand,
			),
		);
		const nearestMoves = pool.filter(
			(candidateInfo) =>
				candidateInfo.distanceToDestinationBand === nearestDistance,
		);
		if (nearestMoves.length > 0 && rng.next() < progress) {
			pool = nearestMoves;
		}
	}
	return (pool[rng.int(pool.length)] ?? pool[0])?.move;
}

function canAppendPathMoveIncrementally(params: {
	current: HexCoord;
	candidate: HexCoord;
	pathSet: Set<string>;
}): boolean {
	const { current, candidate, pathSet } = params;
	let adjacentPathCount = 0;
	let adjacentToCurrent = false;
	for (const neighbor of getNeighbors(candidate.row, candidate.col)) {
		if (!pathSet.has(coordKey(neighbor.row, neighbor.col))) {
			continue;
		}
		adjacentPathCount += 1;
		if (neighbor.row === current.row && neighbor.col === current.col) {
			adjacentToCurrent = true;
		}
		if (adjacentPathCount > 1) {
			return false;
		}
	}
	return adjacentPathCount === 1 && adjacentToCurrent;
}

/**
 * Build a path of exactly `targetLength` tiles from start to end using the
 * HexagonBackground move rules, plus a fixed-length feasibility guard.
 */
export function generateFixedLengthPath(
	config: PathGeneratorConfig,
	targetLength: number,
	rng: SeededRng,
): HexCoord[] | null {
	if (targetLength < 2) {
		return null;
	}

	const { startRow, startCol } = config;

	const path: HexCoord[] = [{ row: startRow, col: startCol }];
	const pathSet = new Set<string>([coordKey(startRow, startCol)]);
	const { xMaxStepsBack = 1, xMaxStepsForward = 5 } = config;
	const maxConsecutiveCenterDistanceMoves = Math.max(
		1,
		Math.round((xMaxStepsBack + xMaxStepsForward) / 2),
	);
	const maxConsecutiveSameDirectionSteps = Math.max(
		1,
		config.maxConsecutiveSameDirectionSteps ??
			DEFAULT_MAX_CONSECUTIVE_SAME_DIRECTION_STEPS,
	);
	let consecutiveAwayFromCenter = 0;
	let consecutiveTowardCenter = 0;
	let previousDirection: HexDirection | null = null;
	let consecutiveSameDirectionSteps = 0;
	const totalSteps = targetLength;

	while (path.length < targetLength) {
		const stepsRemaining = targetLength - path.length;
		const current = path[path.length - 1];
		if (!current) {
			return null;
		}

		const neighbors = getNeighbors(current.row, current.col);
		let validMoves = neighbors.filter(
			(n) => !pathSet.has(coordKey(n.row, n.col)),
		);

		if (path.length > 1) {
			const prevHex = path[path.length - 2];
			if (prevHex) {
				const prevNeighbors = getNeighbors(prevHex.row, prevHex.col);
				const prevNeighborsSet = new Set(
					prevNeighbors.map((n) => coordKey(n.row, n.col)),
				);
				validMoves = validMoves.filter(
					(n) => !prevNeighborsSet.has(coordKey(n.row, n.col)),
				);
			}
		}

		validMoves = filterValidPathMoves(validMoves, current, config);

		const candidateInfos = validMoves
			.filter((move) =>
				canAppendPathMoveIncrementally({
					current,
					candidate: move,
					pathSet,
				}),
			)
			.map((move) => {
				const distanceToBand = distanceToDestinationBand(
					move.row,
					move.col,
					config,
				);
				return {
					move,
					distanceToDestinationBand: distanceToBand,
					movesAwayFromCenter: isMoveAwayFromCenter(
						current.col,
						move.col,
						config.endCol,
					),
					movesTowardCenter: isMoveTowardCenter(
						current.col,
						move.col,
						config.endCol,
					),
					direction: getDirection(
						current.row,
						current.col,
						move.row,
						move.col,
					),
				} satisfies MoveCandidateInfo;
			})
			.filter((candidateInfo) =>
				isFeasibleStep(
					candidateInfo.distanceToDestinationBand,
					candidateInfo.move,
					stepsRemaining - 1,
					config,
				),
			);

		if (candidateInfos.length === 0) {
			return null;
		}

		const chosenMove = pickNextMove(
			candidateInfos,
			rng,
			totalSteps <= 1 ? 1 : path.length / totalSteps,
			consecutiveAwayFromCenter,
			consecutiveTowardCenter,
			maxConsecutiveCenterDistanceMoves,
			previousDirection,
			consecutiveSameDirectionSteps,
			maxConsecutiveSameDirectionSteps,
		);
		if (!chosenMove) {
			return null;
		}

		const isChosenAwayFromCenter = isMoveAwayFromCenter(
			current.col,
			chosenMove.col,
			config.endCol,
		);
		const isChosenTowardCenter = isMoveTowardCenter(
			current.col,
			chosenMove.col,
			config.endCol,
		);
		const chosenDirection = getDirection(
			current.row,
			current.col,
			chosenMove.row,
			chosenMove.col,
		);
		path.push(chosenMove);
		pathSet.add(coordKey(chosenMove.row, chosenMove.col));
		if (isChosenAwayFromCenter) {
			consecutiveAwayFromCenter += 1;
			consecutiveTowardCenter = 0;
		} else if (isChosenTowardCenter) {
			consecutiveTowardCenter += 1;
			consecutiveAwayFromCenter = 0;
		} else {
			consecutiveAwayFromCenter = 0;
			consecutiveTowardCenter = 0;
		}
		if (previousDirection === chosenDirection) {
			consecutiveSameDirectionSteps += 1;
		} else {
			previousDirection = chosenDirection;
			consecutiveSameDirectionSteps = 1;
		}
	}

	const last = path[path.length - 1];
	if (path.length !== targetLength || !last) {
		return null;
	}
	if (!isDestinationCell(last.row, last.col, config)) {
		return null;
	}
	if (
		path.some(
			(step) => !isWithinStartEndCorridor(step.row, step.col, config),
		)
	) {
		return null;
	}
	if (!isValidPath(path)) {
		return null;
	}
	return path;
}

export function generatePathWithAttempts(
	options: PathGenerationAttemptOptions,
): PathGenerationResult {
	const maxAttempts =
		options.maxAttempts ?? DEFAULT_PATH_GENERATION_MAX_ATTEMPTS;
	const { seed, targetLength, config, logAttempts = true } = options;

	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		const attemptSeed = `${seed}:attempt:${attempt}`;
		const rng = createSeededRng(attemptSeed);
		const path = generateFixedLengthPath(config, targetLength, rng);
		if (logAttempts) {
			// Testing visibility — remove or gate behind flag when no longer needed.
			console.info(
				`[hex-path] path generation attempt ${attempt}/${maxAttempts} seed=${seed} length=${targetLength} ${path ? "success" : "failed"}`,
			);
		}
		if (path) {
			return { path, attempt };
		}
	}

	throw new Error(
		`[hex-path] Failed to generate valid path after ${maxAttempts} attempts (seed=${seed}, length=${targetLength})`,
	);
}
